import { Controller } from "@hotwired/stimulus";
import * as d3 from "d3";

// Ctrl+wheel (also how browsers report a trackpad pinch) zooms, anchored at the cursor, the
// same convention Miro/Figma/Google Maps use; plain wheel/drag keeps panning via native scroll.
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2.5;
const ZOOM_SENSITIVITY = 0.002;

export default class extends Controller {
  static values = {
    data: Object,
    height: Number,
    nodeTemplate: String,
    nodeTemplates: Object,
    adminView: Boolean,
  };

  connect() {
    this.initializeCanvas();
    this.observeViewportSize();
    this.requestTreeRender();
  }

  // The container is sized by the page layout; re-layout the tree whenever that viewport changes.
  observeViewportSize() {
    this.lastViewportSize = { width: this.element.clientWidth, height: this.element.clientHeight };

    this.resizeObserver = new ResizeObserver(() => {
      const width = this.element.clientWidth;
      const height = this.element.clientHeight;

      if (width === this.lastViewportSize.width && height === this.lastViewportSize.height) {
        return;
      }

      this.lastViewportSize = { width, height };
      this.requestTreeRender();
    });
    this.resizeObserver.observe(this.element);
  }

  dataValueChanged() {
    if (this.element.isConnected) {
      this.requestTreeRender();
    }
  }

  heightValueChanged() {
    if (this.element.isConnected) {
      this.requestTreeRender();
    }
  }

  nodeTemplateValueChanged() {
    if (this.element.isConnected) {
      this.requestTreeRender();
    }
  }

  nodeTemplatesValueChanged() {
    if (this.element.isConnected) {
      this.requestTreeRender();
    }
  }

  disconnect() {
    if (this.pendingRenderFrame) {
      cancelAnimationFrame(this.pendingRenderFrame);
      this.pendingRenderFrame = null;
    }

    if (this.pendingAnimationFrame) {
      cancelAnimationFrame(this.pendingAnimationFrame);
      this.pendingAnimationFrame = null;
    }

    if (this.highlightTimeout) {
      clearTimeout(this.highlightTimeout);
      this.highlightTimeout = null;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.teardownPanHandlers();
    this.teardownZoomHandlers();
    this.teardownLazyObserver();

    this.element.innerHTML = "";
    this.canvas = null;
    this.svg = null;
    this.linksGroup = null;
    this.nodesLayer = null;
    this.previousNodePositions = null;
  }

  scrollToAgentNode(agentId) {
    if (agentId === undefined || agentId === null || agentId === "") {
      return false;
    }

    const selector = `.tree-node[data-agent-id="${CSS.escape(String(agentId))}"]`;
    const nodeElement = this.nodesLayer?.node()?.querySelector(selector);

    if (!nodeElement) {
      return false;
    }

    const nodeRect = nodeElement.getBoundingClientRect();
    const containerRect = this.element.getBoundingClientRect();

    const deltaX =
      nodeRect.left + nodeRect.width / 2 - (containerRect.left + this.element.clientWidth / 2);
    const deltaY =
      nodeRect.top + nodeRect.height / 2 - (containerRect.top + this.element.clientHeight / 2);

    this.element.scrollTo({
      left: this.element.scrollLeft + deltaX,
      top: this.element.scrollTop + deltaY,
      behavior: "smooth",
    });

    this.highlightNode(nodeElement);

    return true;
  }

  highlightNode(nodeElement) {
    const content = nodeElement.querySelector(".tree-node-content");
    if (!content) {
      return;
    }

    if (this.highlightTimeout) {
      clearTimeout(this.highlightTimeout);
    }

    const highlightClasses = ["ring-2", "ring-offset-2", "ring-yellow-400"];
    content.classList.add(...highlightClasses);

    this.highlightTimeout = setTimeout(() => {
      content.classList.remove(...highlightClasses);
      this.highlightTimeout = null;
    }, 1800);
  }

  requestTreeRender() {
    if (this.pendingRenderFrame) {
      return;
    }

    this.pendingRenderFrame = requestAnimationFrame(() => {
      this.pendingRenderFrame = null;

      if (!this.element.isConnected) {
        return;
      }

      this.updateTree(this.currentData());
    });
  }

  initializeCanvas() {
    if (this.canvas) {
      return;
    }

    this.element.innerHTML = "";
    this.element.style.position = "relative";
    this.element.style.overflowX = "auto";
    this.element.style.overflowY = "auto";
    this.element.style.cursor = "grab";
    this.element.style.touchAction = "none";

    this.canvas = d3
      .select(this.element)
      .append("div")
      .attr("class", "tree-canvas")
      .style("position", "relative")
      .style("transform-origin", "0 0")
      .style("transform", "scale(1)");

    this.svg = this.canvas
      .append("svg")
      .style("position", "absolute")
      .style("left", "0")
      .style("top", "0");

    this.linksGroup = this.svg.append("g");

    this.nodesLayer = this.canvas
      .append("div")
      .attr("class", "tree-nodes-layer")
      .style("position", "absolute")
      .style("left", "0")
      .style("top", "0");

    this.zoomLevel = 1;
    this.setupPanHandlers();
    this.setupZoomHandlers();
  }

  currentData() {
    if (this.hasDataValue) {
      return this.dataValue;
    }

    return {};
  }

  appendChildNode(parentId, childNode) {
    if (!childNode || parentId === undefined || parentId === null) {
      return;
    }

    const nextData = structuredClone(this.currentData());
    const parentNode = this.findNodeById(nextData, parentId);

    if (!parentNode) {
      return;
    }

    if (this.findNodeById(nextData, childNode.id)) {
      return;
    }

    parentNode.children ||= [];
    parentNode.children.push(childNode);
    this.dataValue = nextData;
  }

  updateNode(nodeData) {
    const nodeId = nodeData?.id;
    if (!nodeId) {
      return;
    }

    const nextData = structuredClone(this.currentData());
    const existingNode = this.findNodeById(nextData, nodeId);

    if (!existingNode) {
      return;
    }

    const existingChildren = existingNode.children;
    Object.assign(existingNode, nodeData);

    if (!Object.prototype.hasOwnProperty.call(nodeData, "children")) {
      existingNode.children = existingChildren;
    }

    this.dataValue = nextData;
  }

  removeNode(nodeId) {
    if (nodeId === undefined || nodeId === null) {
      return;
    }

    const nextData = structuredClone(this.currentData());
    if (String(nextData?.id) === String(nodeId)) {
      return;
    }

    if (!this.removeChildNodeById(nextData, nodeId)) {
      return;
    }

    this.dataValue = nextData;
  }

  // The level node takes the place of the given siblings, which move below it.
  insertLevelNode(childIds, levelNode) {
    if (!levelNode || !Array.isArray(childIds) || childIds.length === 0) {
      return;
    }

    const nextData = structuredClone(this.currentData());
    if (this.findNodeById(nextData, levelNode.id)) {
      return;
    }

    const siblings = this.findSiblingsOf(nextData, childIds[0]);
    if (!siblings) {
      return;
    }

    const ids = childIds.map(String);
    const isMoved = (node) => ids.includes(String(node.id));
    const levelIndex = siblings.findIndex(isMoved);
    const movedNodes = siblings.filter(isMoved);

    for (let index = siblings.length - 1; index >= 0; index--) {
      if (isMoved(siblings[index])) siblings.splice(index, 1);
    }
    siblings.splice(levelIndex, 0, { ...levelNode, children: movedNodes });
    this.dataValue = nextData;
  }

  unwrapNode(nodeId) {
    if (nodeId === undefined || nodeId === null) {
      return;
    }

    const nextData = structuredClone(this.currentData());
    const siblings = this.findSiblingsOf(nextData, nodeId);
    if (!siblings) {
      return;
    }

    const nodeIndex = siblings.findIndex((node) => String(node.id) === String(nodeId));
    siblings.splice(nodeIndex, 1, ...(siblings[nodeIndex].children || []));
    this.dataValue = nextData;
  }

  updateNodeTemplates(nodeTemplates) {
    if (!nodeTemplates || typeof nodeTemplates !== "object") {
      return;
    }

    this.nodeTemplatesValue = {
      ...(this.hasNodeTemplatesValue ? this.nodeTemplatesValue : {}),
      ...nodeTemplates,
    };
  }

  updateTree(data) {
    this.initializeCanvas();

    const containerWidth = this.element.clientWidth || 640;
    const requestedHeight = this.element.clientHeight || 720;
    const nodeWidth = 200;
    const nodeHeight = 240;
    const structureLabelHeight = 24;
    const margin = {
      top: Math.max(24, nodeHeight / 2 + 12 + structureLabelHeight),
      right: 40,
      bottom: 24,
      left: 40,
    };
    const horizontalGap = 40;
    const levelGap = nodeHeight + 48;
    const siblingSpacing = 1.6;
    const cousinSpacing = 2.2;
    const innerLayoutHeight = Math.max(1, requestedHeight - margin.top - margin.bottom);

    const root = d3.hierarchy(data);
    this.assignNodeKeys(root);

    const leafCount = Math.max(root.leaves().length, 1);
    const requiredCanvasWidth =
      leafCount * nodeWidth + (leafCount - 1) * horizontalGap + margin.left + margin.right;
    const canvasWidth = Math.max(containerWidth, requiredCanvasWidth);

    d3
      .tree()
      .size([
        canvasWidth - margin.left - margin.right,
        innerLayoutHeight,
      ])
      .separation((a, b) => (a.parent === b.parent ? siblingSpacing : cousinSpacing))(root);

    root.descendants().forEach((d) => {
      d.y = d.depth * levelGap;
    });

    const layoutHeight = Math.max(
      requestedHeight,
      root.height * levelGap + margin.top + margin.bottom + nodeHeight,
    );

    this.canvas
      .style("width", `${canvasWidth}px`)
      .style("height", `${layoutHeight}px`);

    this.svg
      .attr("viewBox", `0 0 ${canvasWidth} ${layoutHeight}`)
      .attr("width", canvasWidth)
      .attr("height", layoutHeight)
      .style("left", "0")
      .style("top", "0");

    this.linksGroup.attr("transform", `translate(${margin.left}, ${margin.top})`);

    const transition = d3.transition().duration(260).ease(d3.easeCubicOut);

    this.linksGroup
      .selectAll("path.link")
      .data(root.links(), (d) => d.target.data._treeKey)
      .join(
        (enter) =>
          enter
            .append("path")
            .attr("class", "link")
            .attr("fill", "none")
            .attr("stroke", "#9ca3af")
            .attr("stroke-width", 1.5)
            .attr("opacity", 0)
            .attr("d", (d) => {
              const from = this.previousPositionFor(d.source);
              return this.linkPath(from.x, from.y, from.x, from.y, nodeHeight);
            })
            .call((selection) =>
              selection
                .transition(transition)
                .attr("opacity", 1)
                .attr("d", (d) => this.linkPath(d.source.x, d.source.y, d.target.x, d.target.y, nodeHeight)),
            ),
        (update) =>
          update.call((selection) =>
            selection
              .transition(transition)
              .attr("d", (d) => this.linkPath(d.source.x, d.source.y, d.target.x, d.target.y, nodeHeight)),
          ),
        (exit) =>
          exit.call((selection) =>
            selection
              .transition(transition)
              .attr("opacity", 0)
              .attr("d", (d) => {
                const to = this.previousPositionFor(d.source);
                return this.linkPath(to.x, to.y, to.x, to.y, nodeHeight);
              })
              .remove(),
          ),
      );

    this.nodesLayer
      .style("left", "0")
      .style("top", "0")
      .style("width", `${canvasWidth}px`)
      .style("height", `${layoutHeight}px`);

    const nodes = this.nodesLayer
      .selectAll("div.tree-node")
      .data(root.descendants(), (d) => d.data._treeKey);

    nodes
      .exit()
      .call((selection) =>
        selection
          .transition(transition)
          .style("opacity", 0)
          .remove(),
      );

    const nodesEnter = nodes
      .enter()
      .append("div")
      .attr("class", "tree-node")
      .style("position", "absolute")
      .style("width", `${nodeWidth}px`)
      .style("height", `${nodeHeight}px`)
      .style("opacity", 0)
      .style("transition", "transform 260ms ease, opacity 260ms ease")
      .style("transform", (d) => {
        const from = this.previousPositionFor(d);
        return this.nodeTransform(from.x, from.y, margin);
      });

    nodesEnter
      .append("div")
      .attr("class", (d) => `tree-node-content ${this.agentTypeBorderColor(d.data?.agent?.type?.raw, d.data?.agent?.blocked)} bg-white px-2 py-1 rounded-lg border-t-2 shadow-sm hover:shadow-lg`)
      .style("width", "100%")
      .style("height", "100%");

    nodesEnter
      .append("div")
      .attr("class", "tree-node-structure absolute bottom-full left-1/2 -translate-x-1/2 mb-1 max-w-full truncate rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-tight");

    const nodesMerged = nodesEnter.merge(nodes)
      .attr("data-lazy-node-id", (d) => d.data._lazy ? String(d.data.id) : null)
      .attr("data-agent-id", (d) => (d.data?.agent?.id !== undefined && d.data?.agent?.id !== null) ? String(d.data.agent.id) : null)
      .style("transition", "transform 260ms ease, opacity 260ms ease")
      .style(
        "transform",
        (d) => this.nodeTransform(d.x, d.y, margin),
      );

    nodesMerged
      .select(".tree-node-content")
      .attr("class", (d) => `tree-node-content ${this.agentTypeBorderColor(d.data?.agent?.type?.raw, d.data?.agent?.blocked)} bg-white px-2 py-1 rounded-lg border-t-2 shadow-sm hover:shadow-lg`)
      .html((d) => this.renderNodeContent(d))
      .each((d, index, elements) => {
        if (this.isLockedHeadNode(d)) this.lockNodeContent(elements[index], d);
        else this.lockWriteProtectedContent(elements[index], d);
      });

    // Every node names its structure; where one starts (the tree's top, each attached substructure) it stands out.
    nodesMerged
      .select(".tree-node-structure")
      .classed("hidden", (d) => !d.data?.structure_name)
      .classed("bg-indigo-50 text-indigo-700", (d) => Boolean(d.data?.structure_transition))
      .classed("bg-white text-slate-400", (d) => !d.data?.structure_transition)
      .attr("title", (d) => d.data?.structure_name ?? null)
      .text((d) => d.data?.structure_name ?? "");

    if (this.pendingAnimationFrame) {
      cancelAnimationFrame(this.pendingAnimationFrame);
    }

    this.pendingAnimationFrame = requestAnimationFrame(() => {
      nodesMerged
        .style("opacity", 1)
        .style("transform", (d) => this.nodeTransform(d.x, d.y, margin));
    });

    this.previousNodePositions = {};
    root.descendants().forEach((node) => {
      this.previousNodePositions[node.data._treeKey] = { x: node.x, y: node.y };
    });

    this.observeLazyNodes(root);
  }

  assignNodeKeys(root) {
    root.each((node) => {
      if (!node.parent) {
        node.data._treeKey = String(node.data.id ?? node.data.name ?? "root");
        return;
      }

      const siblingIndex = node.parent.children.indexOf(node);
      const localKey = String(node.data.id ?? `${node.data.name ?? "node"}-${siblingIndex}`);
      node.data._treeKey = `${node.parent.data._treeKey}/${localKey}`;
    });
  }

  findNodeById(node, id) {
    if (!node) {
      return null;
    }

    if (String(node.id) === String(id)) {
      return node;
    }

    const children = Array.isArray(node.children) ? node.children : [];
    for (const child of children) {
      const match = this.findNodeById(child, id);
      if (match) {
        return match;
      }
    }

    return null;
  }

  // null for the root or a missing node.
  findSiblingsOf(node, id) {
    const children = Array.isArray(node?.children) ? node.children : [];
    if (children.some((child) => String(child.id) === String(id))) {
      return children;
    }

    for (const child of children) {
      const siblings = this.findSiblingsOf(child, id);
      if (siblings) {
        return siblings;
      }
    }

    return null;
  }

  removeChildNodeById(node, id) {
    const children = Array.isArray(node?.children) ? node.children : [];
    const childIndex = children.findIndex((child) => String(child.id) === String(id));

    if (childIndex >= 0) {
      children.splice(childIndex, 1);
      return true;
    }

    for (const child of children) {
      if (this.removeChildNodeById(child, id)) {
        return true;
      }
    }

    return false;
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  defaultNodeTemplate() {
    return `
      <div class="flex h-full w-full items-center justify-between gap-2 text-xs text-slate-900">
      </div>
    `;
  }

  nodeTemplateFor(nodeData) {
    const agentGroupId = nodeData?.agent_group_id;
    const mergeCandidatesAllowed = nodeData?.merge_candidates_allowed ? 1 : 0;
    const variantKey = `${agentGroupId}:${mergeCandidatesAllowed}`;

    if (agentGroupId !== undefined && agentGroupId !== null && this.hasNodeTemplatesValue) {
      const template = this.nodeTemplatesValue[variantKey] ?? this.nodeTemplatesValue[String(agentGroupId)];
      if (typeof template === "string") {
        return template;
      }
    }

    if (this.hasNodeTemplateValue) {
      return this.nodeTemplateValue;
    }

    if (this.hasNodeTemplatesValue) {
      return Object.values(this.nodeTemplatesValue).find((template) => typeof template === "string");
    }

    return this.defaultNodeTemplate();
  }

  renderNodeContent(node) {
    const agent_id = node.data?.agent?.id;
    const agent_name = node.data?.agent?.name;
    const isRoot = node.data?.root ?? false;
    const selectableAssistants = node.data?.selectable_assistants || [];
    const rawAgentType = node.data?.agent?.type?.raw;

    let template = this.nodeTemplateFor(node.data);

    if (isRoot || node.data?.structure_head) {
      template = template.replaceAll("delete-btn", "delete-btn hidden");
    }
    // The person of a structure's top box follows the structure settings.
    if (node.data?.structure_head) {
      template = template
        .replaceAll(`name="agent_id"`, `name="agent_id" disabled`)
        .replaceAll("structure-head-hint hidden", "structure-head-hint");
    }
    if (agent_id) {
      template = template.replace('--</option>', `--</option><option value="${agent_id}" selected>${this.escapeHtml(agent_name)}</option>`);
    }

    selectableAssistants.reverse().forEach(([assistantName, assistantId]) => {
      template = template.replace('--</option>', `--</option><option value="${assistantId}">${this.escapeHtml(assistantName)}</option>`);
    });

    if (["Assistant"].includes(rawAgentType) || isRoot) {
      template = template.replaceAll("is-head-label", "is-head-label hidden");
    }
    // Structure heads are set on the agent group
    if(["Root"].includes(rawAgentType)) {
      template = template.replaceAll("is-head-label cursor-pointer", "is-head-label cursor-not-allowed");
    }
    const readOnlyToggleable = this.adminViewValue
      ? node.data?.is_head || (["Root"].includes(rawAgentType) && !isRoot)
      : this.isOwnReadOnlyToggle(node);
    if (readOnlyToggleable) {
      template = template.replaceAll("read-only-label hidden", "read-only-label flex cursor-pointer");
    } else if (node.data?.read_only) {
      // Superiors see the protection but can't lift it.
      template = template
        .replaceAll("read-only-label hidden", "read-only-label flex cursor-not-allowed")
        .replaceAll(`name="read_only"`, `name="read_only" disabled`);
    }

    if (node.data?.is_head || ["Root"].includes(rawAgentType)) {
      template = template.replaceAll(`name="is_head"`, `name="is_head" checked`)
    }
    if (node.data?.read_only) {
      template = template.replaceAll(`name="read_only"`, `name="read_only" checked`);
    }

    if (node.data?._lazy) {
      template = template.replaceAll("lazy-loading-indicator hidden", "lazy-loading-indicator");
      template = template.replaceAll("add-subnode-form ", "add-subnode-form hidden ");
    }
    if (node.data?.agent?.blocked) {
      template = template.replaceAll("blocked hidden", "blocked flex");
    }

    return template
      .replaceAll("::id::", node.data?.id ?? "")
      .replaceAll("{{agent_type_text_color}}", this.escapeHtml(this.agentTypeTextColor(rawAgentType, node.data?.agent?.blocked) ?? ""))
      .replaceAll("{{agent.type.translated}}", this.escapeHtml(node.data?.agent?.type?.translated ?? ""))
      .replaceAll("{{agent.id}}", agent_id ?? "")
      .replaceAll("{{direct_commission_rate}}", node.data?.direct_commission_rate ?? "")
      .replaceAll("{{factoring_commission_rate}}", node.data?.factoring_commission_rate ?? "")
      .replaceAll("{{recurring_commission_rate}}", node.data?.recurring_commission_rate ?? "")
  }

  // Outside the admin view the head node is read-only; subnodes can still be added.
  isLockedHeadNode(node) {
    return !this.adminViewValue && (node.data?.root ?? false);
  }

  // A sub-head sets Schreibgeschützt on their own (head) node.
  isOwnReadOnlyToggle(node) {
    return this.isLockedHeadNode(node) && (node.data?.is_head ?? false);
  }

  // Runs before slim-select attaches, so the select renders disabled.
  lockNodeContent(content, node) {
    // _method and the token have to stay so the read_only toggle still submits as a PATCH.
    const controls = this.isOwnReadOnlyToggle(node)
      ? ':is(input:not([name="read_only"], [name="_method"], [name="authenticity_token"]), select, button)'
      : ":is(input, select, button)";
    content
      .querySelectorAll(`form:not(.add-subnode-form) ${controls}`)
      .forEach((control) => {
        control.disabled = true;
      });
  }

  // Superiors can't change anything below a Schreibgeschützt node, nor the node itself apart from its rates.
  lockWriteProtectedContent(content, node) {
    if (this.adminViewValue) return;

    // The tree's root is the viewer's own node; a protection there or above doesn't apply to them.
    const belowProtected = node.ancestors().slice(1, -1).some((ancestor) => ancestor.data?.read_only);
    let controls;
    if (belowProtected) {
      controls = "form :is(input, select, button)";
    } else if (node.data?.read_only) {
      // The rates submit with the form's _method and token, so those stay.
      controls = 'form :is(select, input[name="is_head"], button)';
    } else {
      return;
    }

    content.querySelectorAll(controls).forEach((control) => {
      control.disabled = true;
    });
    content.querySelectorAll(".delete-btn, .add-subnode-form button, .is-head-label").forEach((control) => {
      control.classList.add("opacity-50", "cursor-not-allowed");
      control.classList.remove("cursor-pointer");
    });
  }

  agentTypeTextColor(agentType, blocked = false) {
    if (blocked) {
      return "text-slate-400";
    }

    switch (agentType) {
      case "Root":
        return "text-indigo-600";
      case "Head":
        return "text-purple-600";
      case "Agent":
        return "text-blue-600";
      case "Assistant":
        return "text-amber-600";
      default:
        return "text-gray-600";
    }
  }

  agentTypeBorderColor(agentType, blocked = false) {
    if (blocked) {
      return "border-slate-400";
    }

    switch (agentType) {
      case "Root":
        return "border-indigo-600";
      case "Head":
        return "border-purple-600";
      case "Agent":
        return "border-blue-600";
      case "Assistant":
        return "border-amber-600";
      default:
        return "border-slate-300";
    }
  }

  previousPositionFor(node) {
    const positions = this.previousNodePositions || {};
    const ownPosition = positions[node.data._treeKey];
    if (ownPosition) {
      return ownPosition;
    }

    if (node.parent) {
      const parentPosition = positions[node.parent.data._treeKey];
      if (parentPosition) {
        return parentPosition;
      }
    }

    return { x: node.x, y: node.y };
  }

  nodeTransform(x, y, margin) {
    return `translate(${x + margin.left}px, ${y + margin.top}px) translate(-50%, -50%)`;
  }

  linkPath(sourceX, sourceY, targetX, targetY, nodeHeight) {
    const sourceYWithOffset = sourceY + nodeHeight / 2;
    const targetYWithOffset = targetY - nodeHeight / 2;
    const midY = sourceYWithOffset + (targetYWithOffset - sourceYWithOffset) / 2;
    return `M${sourceX},${sourceYWithOffset} V${midY} H${targetX} V${targetYWithOffset}`;
  }

  setupPanHandlers() {
    if (this.panHandlersBound) {
      return;
    }

    this.onPointerDown = this.handlePanPointerDown.bind(this);
    this.onPointerMove = this.handlePanPointerMove.bind(this);
    this.onPointerUp = this.handlePanPointerUp.bind(this);

    this.element.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointercancel", this.onPointerUp);
    this.panHandlersBound = true;
  }

  teardownPanHandlers() {
    if (!this.panHandlersBound) {
      return;
    }

    this.element.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerUp);

    this.panHandlersBound = false;
    this.isPanning = false;
  }

  handlePanPointerDown(event) {
    if (event.button !== 0) {
      return;
    }

    if (this.isNodePointerEvent(event)) {
      return;
    }

    this.isPanning = true;
    this.panPointerId = event.pointerId;
    this.panStartX = event.clientX;
    this.panStartY = event.clientY;
    this.startScrollLeft = this.element.scrollLeft;
    this.startScrollTop = this.element.scrollTop;
    this.element.style.cursor = "grabbing";
    this.element.style.userSelect = "none";
    this.element.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  handlePanPointerMove(event) {
    if (!this.isPanning || event.pointerId !== this.panPointerId) {
      return;
    }

    const deltaX = event.clientX - this.panStartX;
    const deltaY = event.clientY - this.panStartY;
    this.element.scrollLeft = this.startScrollLeft - deltaX;
    this.element.scrollTop = this.startScrollTop - deltaY;
  }

  handlePanPointerUp(event) {
    if (!this.isPanning || event.pointerId !== this.panPointerId) {
      return;
    }

    this.isPanning = false;
    this.panPointerId = null;
    this.element.style.cursor = "grab";
    this.element.style.userSelect = "";
  }

  isNodePointerEvent(event) {
    const target = event.target;
    return target instanceof Element && Boolean(target.closest(".tree-node"));
  }

  setupZoomHandlers() {
    if (this.zoomHandlersBound) {
      return;
    }

    this.onWheelZoom = this.handleWheelZoom.bind(this);
    this.element.addEventListener("wheel", this.onWheelZoom, { passive: false });
    this.zoomHandlersBound = true;
  }

  teardownZoomHandlers() {
    if (!this.zoomHandlersBound) {
      return;
    }

    this.element.removeEventListener("wheel", this.onWheelZoom);
    this.zoomHandlersBound = false;
  }

  // Ctrl+wheel only: plain wheel keeps scrolling/panning the viewport as before. Zooms anchored at
  // the cursor, so whatever point is under it stays there - the same feel as Miro/Figma/Google Maps.
  handleWheelZoom(event) {
    if (!event.ctrlKey) {
      return;
    }

    event.preventDefault();

    const oldZoom = this.zoomLevel ?? 1;
    const zoomFactor = Math.exp(-event.deltaY * ZOOM_SENSITIVITY);
    const newZoom = this.clampZoom(oldZoom * zoomFactor);
    if (newZoom === oldZoom) {
      return;
    }

    const containerRect = this.element.getBoundingClientRect();
    const pointerX = event.clientX - containerRect.left;
    const pointerY = event.clientY - containerRect.top;

    // The content-space point currently under the cursor, independent of zoom level.
    const contentX = (this.element.scrollLeft + pointerX) / oldZoom;
    const contentY = (this.element.scrollTop + pointerY) / oldZoom;

    this.zoomLevel = newZoom;
    this.canvas.style("transform", `scale(${newZoom})`);

    // Re-anchor that same content point under the cursor at the new zoom level.
    this.element.scrollLeft = contentX * newZoom - pointerX;
    this.element.scrollTop = contentY * newZoom - pointerY;
  }

  clampZoom(value) {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
  }

  observeLazyNodes(root) {
    this.teardownLazyObserver();

    const lazyNodes = root.descendants().filter((d) => d.data._lazy);
    if (lazyNodes.length === 0) return;

    this.lazyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const nodeId = entry.target.dataset.lazyNodeId;
          if (!nodeId || this.lazyLoadingIds?.has(nodeId)) continue;

          this.lazyObserver?.unobserve(entry.target);
          this.loadLazyChildren(nodeId);
        }
      },
      { root: this.element, rootMargin: "200px" },
    );

    this.lazyLoadingIds ||= new Set();

    const nodeElements = this.nodesLayer?.node()?.querySelectorAll(".tree-node") || [];
    const lazyIds = new Set(lazyNodes.map((d) => String(d.data.id)));

    for (const el of nodeElements) {
      const nodeId = el.dataset.lazyNodeId;
      if (nodeId && lazyIds.has(nodeId) && !this.lazyLoadingIds.has(nodeId)) {
        this.lazyObserver.observe(el);
      }
    }
  }

  teardownLazyObserver() {
    if (this.lazyObserver) {
      this.lazyObserver.disconnect();
      this.lazyObserver = null;
    }
  }

  loadLazyChildren(nodeId) {
    this.lazyLoadingIds ||= new Set();
    this.lazyLoadPromises ||= new Map();

    const existing = this.lazyLoadPromises.get(String(nodeId));
    if (existing) {
      return existing;
    }

    this.lazyLoadingIds.add(String(nodeId));

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    const promise = fetch(`/agent_ranks/${encodeURIComponent(nodeId)}/children`, {
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((children) => {
        const nextData = structuredClone(this.currentData());
        const parentNode = this.findNodeById(nextData, Number(nodeId));
        if (!parentNode) return;

        parentNode.children = children;
        delete parentNode._lazy;
        delete parentNode._children_count;
        this.dataValue = nextData;
      })
      .catch((error) => {
        console.error("Failed to load lazy children", error);
      })
      .finally(() => {
        this.lazyLoadingIds?.delete(String(nodeId));
        this.lazyLoadPromises?.delete(String(nodeId));
      });

    this.lazyLoadPromises.set(String(nodeId), promise);
    return promise;
  }

  // Reveals an agent's node, expanding any lazily-loaded ancestors along the
  // way. Resolves to true once the node is scrolled into view, false if the
  // agent isn't present in this structure tree.
  async revealAgentNode(agentId) {
    if (this.scrollToAgentNode(agentId)) {
      return true;
    }

    const rootId = this.currentData()?.id;
    if (rootId === undefined || rootId === null) {
      return false;
    }

    let path;
    try {
      path = await this.locateAgentPath(rootId, agentId);
    } catch (error) {
      console.error("Failed to locate agent in tree", error);
      return false;
    }

    if (!Array.isArray(path) || path.length === 0) {
      return false;
    }

    for (const rankId of path) {
      let node = this.findNodeById(this.currentData(), rankId);

      if (!node) {
        // The ancestor isn't loaded yet; give the previous expansion a frame
        // to render, then retry.
        await this.waitForRender();
        node = this.findNodeById(this.currentData(), rankId);
      }

      if (node?._lazy) {
        await this.loadLazyChildren(rankId);
        await this.waitForRender();
      }

      if (this.scrollToAgentNode(agentId)) {
        return true;
      }
    }

    await this.waitForRender();
    return this.scrollToAgentNode(agentId);
  }

  async locateAgentPath(rootId, agentId) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const url = `/agent_ranks/${encodeURIComponent(rootId)}/locate?agent_id=${encodeURIComponent(agentId)}`;

    const response = await fetch(url, {
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data?.found ? data.path : null;
  }

  waitForRender() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }
}